import React from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  ArrowRight, MapPin, Phone, Users, ShieldCheck, ClipboardCheck, Info,
  MessageSquareText, Package, Gauge, TrendingUp, HeartHandshake, Sparkles, CheckCircle2,
  FileText, Scale, PencilLine, Globe2, Facebook, Instagram, Globe, Star, Check, Minus, AlertTriangle,
} from "lucide-react";
import { Container, SectionHeading, PrimaryLink } from "@/components/ui-kit";
import { getBusinessHook } from "@/data/whyThisMatters";
import { getPlanPrice } from "@/data/content";
import { isTagalogSelected, translateExactText } from "@/data/tagalogTranslations";

const PRESENCE_STATUS = {
  good: { Icon: Check, className: "text-[#10B981]", label: "Active" },
  weak: { Icon: AlertTriangle, className: "text-amber-500", label: "Weak / unclear" },
  bad: { Icon: AlertTriangle, className: "text-amber-500", label: "Has problems" },
  fragmented: { Icon: AlertTriangle, className: "text-amber-500", label: "Fragmented" },
  none: { Icon: Minus, className: "text-[#CBD5E1]", label: "Not found" },
};

function PresenceRow({ Icon: RowIcon, label, status }) {
  const s = PRESENCE_STATUS[status] || PRESENCE_STATUS.none;
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl bg-white border border-[#E2E8F0] px-5 py-3.5">
      <span className="flex items-center gap-2.5 text-sm font-medium text-[#334155]"><RowIcon size={16} className="text-[#94A3B8]" /> {label}</span>
      <span className={`flex items-center gap-1.5 text-xs font-semibold ${s.className}`}><s.Icon size={14} /> {s.label}</span>
    </div>
  );
}

export default function WhyThisMatters() {
  const [params] = useSearchParams();
  const slug = params.get("biz") || "";
  const hook = getBusinessHook(slug);
  const rep = hook.reputation;
  const responsibilityPrice = getPlanPrice("responsibility", "ph");
  const ownershipPrice = getPlanPrice("ownership", "ph");
  const contactHref = `/ph/contact?plan=responsibility-plan&concept=${slug}`;
  const isTL = isTagalogSelected();
  const displayName = isTL ? translateExactText(hook.name) : hook.name;

  return (
    <div data-testid="why-this-matters-page">
      {/* HERO */}
      <section className="bg-[#0F172A] relative overflow-hidden">
        <Container className="py-20 md:py-28 text-center relative z-10">
          <span className="inline-block text-xs font-semibold uppercase tracking-[0.18em] text-[#34D399] mb-4">
            Your business already has a reputation.
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-display text-white text-balance max-w-4xl mx-auto">
            Your online presence should earn the same trust as your business.
          </h1>
          {hook.opening && (
            <p className="mt-7 text-xl text-white font-semibold max-w-2xl mx-auto leading-relaxed" data-testid="wtm-opening">{hook.opening}</p>
          )}
          <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Customers don't only ask "what do they sell?" anymore. They ask: are they legitimate, can I trust them,
            where are they, what do other customers say, and exactly what will I get? A professional website gives
            them one official place to find those answers.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="#thought-experiment" data-testid="wtm-cta-see-why" className="inline-flex items-center gap-2 rounded-xl bg-[#10B981] px-7 py-3.5 text-sm font-semibold text-white hover:bg-[#059669] transition-colors">
              See why this matters for your business <ArrowRight size={16} />
            </a>
            {hook.conceptPath && (
              <a href={hook.conceptPath} target="_blank" rel="noopener noreferrer" data-testid="wtm-cta-return-concept" className="inline-flex items-center gap-2 rounded-xl border border-white/20 px-7 py-3.5 text-sm font-semibold text-white hover:bg-white/5 transition-colors">
                &larr; Return to your website concept
              </a>
            )}
          </div>
        </Container>
      </section>

      {/* THOUGHT EXPERIMENT — data-driven digital-presence audit where we have it, generic framing otherwise */}
      <section id="thought-experiment" className="bg-white py-20 md:py-24 scroll-mt-24">
        <Container>
          {hook.presence ? (
            <>
              <SectionHeading center eyebrow="A quick look at your current situation" title="What customers find today." />
              <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto items-start">
                <div className="space-y-2.5" data-testid="wtm-presence-panel">
                  <PresenceRow Icon={Facebook} label="Facebook" status={hook.presence.facebook} />
                  <PresenceRow Icon={Instagram} label="Instagram" status={hook.presence.instagram} />
                  <PresenceRow Icon={Globe} label="Independent website" status={hook.presence.website} />
                  <PresenceRow Icon={Star} label="Public reviews" status={hook.reputation?.rating || hook.reputation?.reviewCount ? "good" : "none"} />
                  {hook.presence.extra && <p className="pt-2 text-xs text-[#64748B] leading-relaxed">{hook.presence.extra}</p>}
                </div>
                <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-7">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#64748B] mb-3">What we found</p>
                  <ul className="space-y-2 text-sm text-[#334155]" data-testid="wtm-findings">
                    {hook.findings?.map((f) => (
                      <li key={f} className="flex items-start gap-2"><span className="mt-1.5 h-1 w-1 rounded-full bg-[#94A3B8] shrink-0" /> {f}</li>
                    ))}
                  </ul>
                </div>
              </div>
              {hook.problem && (
                <div className="mt-8 max-w-2xl mx-auto rounded-2xl border border-[#E2E8F0] bg-white p-8 text-center">
                  <p className="text-[#0F172A] leading-relaxed font-medium" data-testid="wtm-problem">{hook.problem}</p>
                </div>
              )}
            </>
          ) : (
            <>
              <SectionHeading center eyebrow="Put yourself in their shoes" title="Imagine you are the customer." />
              <div className="max-w-2xl mx-auto rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-8 md:p-10 text-center">
                <p className="text-lg text-[#0F172A] leading-relaxed font-medium" data-testid="wtm-risk-framing">{hook.risk}</p>
                <p className="mt-4 text-[#475569] leading-relaxed">
                  You find a Facebook page. There are hundreds of posts. You want to know the exact location, price,
                  availability, policies, and whether other customers trust the business.
                </p>
                <p className="mt-4 text-[#0F172A] font-semibold">How confident are you, before committing?</p>
              </div>
            </>
          )}
        </Container>
      </section>

      {/* WHAT THIS CONCEPT FIXES — business-specific, only where we have it */}
      {hook.fixes && (
        <section className="bg-[#F8FAFC] py-20 md:py-24">
          <Container>
            <SectionHeading center eyebrow="Pointing back at what you just saw" title="Here's what the concept adds." />
            <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
              {hook.fixes.map((f) => (
                <div key={f} className="flex items-start gap-3 rounded-xl bg-white border border-[#E2E8F0] p-5">
                  <CheckCircle2 size={18} className="text-[#10B981] shrink-0 mt-0.5" />
                  <p className="text-sm text-[#334155] leading-relaxed">{f}</p>
                </div>
              ))}
            </div>
            {hook.closing && (
              <p className="mt-10 text-center text-lg font-semibold text-[#0F172A] max-w-xl mx-auto" data-testid="wtm-closing">{hook.closing}</p>
            )}
          </Container>
        </section>
      )}

      {/* CORE COMPARISON */}
      <section className="bg-[#F8FAFC] py-20 md:py-24">
        <Container>
          <SectionHeading center eyebrow="Facebook gets attention. Your website establishes the business." title="Facebook is valuable. But Facebook is not your entire business." />
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="rounded-2xl bg-white border border-[#E2E8F0] p-8" data-testid="wtm-column-social">
              <h3 className="text-lg font-bold font-display text-[#0F172A]">Social Media</h3>
              <p className="mt-1 text-sm text-[#64748B]">Excellent for reach and conversation.</p>
              <ul className="mt-5 space-y-2 text-sm text-[#334155]">
                {["Reaching followers", "Posting updates", "Photos and videos", "Conversations", "Promotions"].map((t) => (
                  <li key={t} className="flex items-center gap-2"><CheckCircle2 size={15} className="text-[#10B981]" /> {t}</li>
                ))}
              </ul>
              <p className="mt-5 text-xs text-[#94A3B8]">But customers may still have to search through posts, comments, Messenger, albums, old announcements, Google results and different social accounts.</p>
            </div>
            <div className="rounded-2xl bg-[#0F172A] border border-[#0F172A] p-8" data-testid="wtm-column-website">
              <h3 className="text-lg font-bold font-display text-white">Your Website</h3>
              <p className="mt-1 text-sm text-slate-400">One official place, controlled by you.</p>
              <ul className="mt-5 space-y-2 text-sm text-slate-200">
                {["What you offer", "Prices or quotations", "Locations & hours", "Reviews", "Team & credentials", "FAQs", "Booking / inquiries", "Contact information"].map((t) => (
                  <li key={t} className="flex items-center gap-2"><CheckCircle2 size={15} className="text-[#34D399]" /> {t}</li>
                ))}
              </ul>
            </div>
          </div>
          <p className="mt-10 text-center text-lg font-semibold text-[#0F172A] max-w-xl mx-auto">
            Don't replace Facebook. Give Facebook somewhere trustworthy to lead customers.
          </p>
        </Container>
      </section>

      {/* TRUST */}
      <section className="bg-white py-20 md:py-24">
        <Container>
          <SectionHeading center eyebrow="Before customers buy, they reduce uncertainty" title={'"Is this business real?"'} />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 max-w-3xl mx-auto text-center">
            {[
              [MapPin, "Real address"], [Phone, "Real phone number"], [Users, "Real team"], [ShieldCheck, "Real customer experiences"],
            ].map(([Icon, t]) => (
              <div key={t} className="rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] p-5">
                <Icon size={20} className="text-[#10B981] mx-auto mb-2" />
                <p className="text-xs font-medium text-[#334155]">{t}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 text-center text-lg font-semibold text-[#0F172A] max-w-xl mx-auto">
            A professional website doesn't manufacture trust. It makes the trust you've already earned easier to verify.
          </p>
        </Container>
      </section>

      {/* REPUTATION */}
      <section className="bg-[#0F172A] py-20 md:py-24">
        <Container>
          <div className="max-w-2xl mx-auto text-center">
            <span className="inline-block text-xs font-semibold uppercase tracking-[0.18em] text-[#34D399] mb-4">You already did the difficult part</span>
            <h2 className="text-3xl sm:text-4xl font-bold font-display text-white text-balance">{hook.name}</h2>
            <div className="mt-6 flex items-center justify-center gap-8" data-testid="wtm-reputation">
              {rep.rating ? (
                <>
                  <div><div className="text-3xl font-bold text-[#34D399] font-display">{rep.rating} &#9733;</div><div className="text-xs text-slate-400 mt-1">Public rating</div></div>
                  <div><div className="text-3xl font-bold text-[#34D399] font-display">{rep.reviewCount}</div><div className="text-xs text-slate-400 mt-1">Public reviews</div></div>
                </>
              ) : (
                <div><div className="text-2xl font-bold text-[#34D399] font-display">{rep.stat}</div><div className="text-xs text-slate-400 mt-1 max-w-xs">{rep.statLabel}</div></div>
              )}
            </div>
            <p className="mt-8 text-slate-300 leading-relaxed">
              Customers have already helped establish this reputation. Why should a new customer have to search
              across Facebook, Google and other platforms to discover it?
            </p>
            <p className="mt-4 text-lg font-semibold text-white">Put your reputation in one professional place.</p>
          </div>
        </Container>
      </section>

      {/* VISUAL PSYCHOLOGY */}
      <section className="bg-[#F8FAFC] py-20 md:py-24">
        <Container>
          <SectionHeading center eyebrow="First impressions happen fast" title="People judge presentation before they know your business." />
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto text-center">
            <div className="rounded-2xl border border-[#E2E8F0] bg-white p-8">
              <h3 className="font-bold text-[#0F172A]">Business A</h3>
              <p className="mt-2 text-sm text-[#64748B]">Facebook-only, scattered presence</p>
            </div>
            <div className="rounded-2xl border-2 border-[#10B981] bg-white p-8">
              <h3 className="font-bold text-[#0F172A]">Business B</h3>
              <p className="mt-2 text-sm text-[#64748B]">Professional website — location, reviews, team, services, contact</p>
            </div>
          </div>
          <p className="mt-8 text-center text-[#475569] max-w-xl mx-auto">
            Both could provide equally good service. If you'd never visited either one before, which one would feel more established?
          </p>
          <p className="mt-4 text-center text-lg font-semibold text-[#0F172A]">
            Your customer doesn't know everything you know about your business. They can only judge what you show them.
          </p>
        </Container>
      </section>

      {/* COST OF DOING NOTHING */}
      <section className="bg-white py-20 md:py-24">
        <Container>
          <SectionHeading center eyebrow="The quiet cost" title="The customer who leaves usually doesn't tell you." />
          <div className="max-w-2xl mx-auto flex items-center justify-center gap-2 text-sm text-[#475569] flex-wrap">
            {["Finds business", "Looks for information", "Can't quickly verify something", "Leaves"].map((t, i) => (
              <React.Fragment key={t}>
                <span className="rounded-full bg-[#F8FAFC] border border-[#E2E8F0] px-4 py-2">{t}</span>
                {i < 3 && <ArrowRight size={14} className="text-[#CBD5E1]" />}
              </React.Fragment>
            ))}
          </div>
          <p className="mt-8 text-center text-[#475569] max-w-xl mx-auto leading-relaxed">
            They don't complain. They don't message you saying "I almost became a customer, but I wasn't confident
            enough." They simply choose somewhere else.
          </p>
        </Container>
      </section>

      {/* NOT JUST A BROCHURE */}
      <section className="bg-[#F8FAFC] py-20 md:py-24">
        <Container>
          <SectionHeading center eyebrow="More than a brochure" title="A modern business website should work for your business." />
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5 max-w-4xl mx-auto">
            {[
              [MessageSquareText, "Answer repetitive questions", "Customers find common answers before Messenger."],
              [Package, "Organize products & services", "No searching through 18 months of posts."],
              [ClipboardCheck, "Capture inquiries", "Customers know exactly how to contact you."],
              [ShieldCheck, "Build credibility", "Show locations, team, credentials and reviews."],
              [Gauge, "Support bookings", "Appointments or reservations where appropriate."],
              [TrendingUp, "Grow with the business", "Accounts, loyalty, payments and more can be added later."],
            ].map(([Icon, t, d]) => (
              <div key={t} className="rounded-2xl bg-white border border-[#E2E8F0] p-6">
                <Icon size={20} className="text-[#10B981] mb-3" />
                <h3 className="font-semibold text-[#0F172A] text-sm">{t}</h3>
                <p className="mt-1.5 text-xs text-[#64748B] leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* WHY MYSITECARE */}
      <section className="bg-[#0F172A] py-20 md:py-24">
        <Container>
          <div className="max-w-2xl mx-auto text-center">
            <span className="inline-block text-xs font-semibold uppercase tracking-[0.18em] text-[#34D399] mb-4">The website is only half the problem</span>
            <h2 className="text-3xl sm:text-4xl font-bold font-display text-white text-balance">Someone has to take responsibility for it.</h2>
            <p className="mt-5 text-slate-300 leading-relaxed">
              Websites need hosting. Updates happen. Things break. Information changes. Security matters. Staff need
              help. And most business owners did not start a business because they wanted another piece of
              technology to manage.
            </p>
            <p className="mt-6 text-lg font-semibold text-white">You run your business. We take responsibility for your website.</p>
          </div>
        </Container>
      </section>

      {/* RECIPROCITY / THE CONCEPT */}
      <section className="bg-white py-20 md:py-24">
        <Container>
          <SectionHeading center eyebrow="Why we built this concept" title="We didn't send you a generic sales pitch." />
          <div className="max-w-2xl mx-auto text-center text-[#475569] leading-relaxed">
            <p>{isTL ? `Sinuri namin ang ${displayName}, kung paano kayo kasalukuyang nahahanap ng mga customer, at ang impormasyong nakikita na ng publiko.` : <>We looked at {hook.name}. We looked at how customers currently find you. We looked at the information already public.</>}</p>
            <p className="mt-3">And instead of simply telling you what we could build, we built a concept so you could see it yourself.</p>
          </div>
          {hook.hero && (
            <div className="mt-8 max-w-2xl mx-auto rounded-2xl overflow-hidden border border-[#E2E8F0] shadow-sm">
              <img src={hook.hero} alt={isTL ? `Preview ng website concept ng ${hook.name}` : `${hook.name} website concept preview`} className="w-full max-h-[320px] object-cover" loading="lazy" data-testid="wtm-concept-preview" />
            </div>
          )}
          <div className="mt-8 max-w-xl mx-auto text-center">
            <p className="text-sm text-[#64748B]">You are under no obligation to use it. We built it because showing you the idea is more useful than sending another message saying "we make websites."</p>
          </div>
        </Container>
      </section>

      {/* DRAFT DISCLOSURE / WHAT CHANGES AFTER YOU SIGN UP */}
      <section className="bg-[#F8FAFC] py-20 md:py-24">
        <Container>
          <SectionHeading center eyebrow="Before you decide" title="What you're looking at is a draft — not the finished website." />
          <div className="max-w-2xl mx-auto text-center text-[#475569] leading-relaxed" data-testid="wtm-draft-notice">
            <p>{isTL ? `Ginawa ang concept na ito upang ipakita ang ideya, hindi upang ibigay bilang tapos na produkto. Susuriin at kukumpirmahin muna kasama ninyo ang mga salita, presyo, oras, serbisyo, at larawan bago maging live ang anumang website sa pangalan ng ${displayName}.` : <>This concept exists to show you an idea, not to hand you a finished product. Wording, pricing, hours, services and photos would all be reviewed and confirmed with you before anything goes live under {hook.name}'s name.</>}</p>
            <p className="mt-3">
              {isTL ? "Kung magpasya kayong magpatuloy sa MySiteCare (" : "If you decide to move forward with MySiteCare ("}
              <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" data-testid="wtm-mysitecare-link" className="text-[#0F172A] font-semibold hover:text-[#10B981]">mysitecare.eu/ph</a>
              {isTL ? "), kasama sa inyong plano ang maayos na pagtatapos ng website—hindi ang pananatili lamang ng draft na ito nang walang pagbabago." : "), your plan includes finishing the website properly — not simply keeping this draft as-is."}
            </p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 md:grid-cols-4 gap-5 max-w-4xl mx-auto">
            {[
              [FileText, "A proper About page", "Your story, told the way you actually want it told."],
              [Scale, "Legal pages", "Terms & Conditions, Privacy Policy and Cookie Policy, done properly."],
              [PencilLine, "Changes & corrections", "Anything the draft got wrong gets fixed together with you."],
              [Globe2, "Room to grow", "Extra pages, features or sections added as the business needs them."],
            ].map(([Icon, t, d]) => (
              <div key={t} className="rounded-2xl bg-white border border-[#E2E8F0] p-6" data-testid={`wtm-draft-item-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <Icon size={20} className="text-[#10B981] mb-3" />
                <h3 className="font-semibold text-[#0F172A] text-sm">{t}</h3>
                <p className="mt-1.5 text-xs text-[#64748B] leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 text-center text-lg font-semibold text-[#0F172A] max-w-xl mx-auto">
            Buying the plan isn't buying this exact draft. It's buying a finished, properly maintained website — built together with you.
          </p>
        </Container>
      </section>

      {/* PRICING */}
      <section className="bg-[#F8FAFC] py-20 md:py-24">
        <Container>
          <SectionHeading center eyebrow="Recommended for established businesses" title="Responsibility Plan" />
          <div className="max-w-md mx-auto rounded-2xl border-2 border-[#10B981] bg-white p-8 text-center shadow-sm" data-testid="wtm-pricing-responsibility">
            <div className="text-4xl font-bold font-display text-[#0F172A]">{responsibilityPrice.price}<span className="text-lg font-medium text-[#64748B]">{responsibilityPrice.period}</span></div>
            <p className="mt-1 text-sm text-[#64748B]">Website included</p>
            <ul className="mt-6 space-y-2 text-sm text-left text-[#334155] max-w-xs mx-auto">
              {["Hosting", "Updates", "Backups", "Monitoring", "Breakage fixes", "One point of responsibility", "Small ongoing changes", "Support"].map((t) => (
                <li key={t} className="flex items-center gap-2"><CheckCircle2 size={15} className="text-[#10B981]" /> {t}</li>
              ))}
            </ul>
            <Link to={contactHref} data-testid="wtm-cta-discuss" className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#10B981] px-7 py-3.5 text-sm font-semibold text-white hover:bg-[#059669] transition-colors">
              Make this my official website <ArrowRight size={16} />
            </Link>
          </div>
          <div className="mt-6 text-center text-sm text-[#64748B]">
            Prefer to own the website and manage it yourself?{" "}
            <Link to="/ph/plans/website-ownership" data-testid="wtm-link-ownership" className="text-[#0F172A] font-semibold hover:text-[#10B981]">Website Ownership from {ownershipPrice.price} &rarr;</Link>
          </div>
          <div className="mt-2 text-center text-sm text-[#64748B]">
            Need more hands-on support? <Link to="/ph/plans" data-testid="wtm-link-all-plans" className="text-[#0F172A] font-semibold hover:text-[#10B981]">See all plans &rarr;</Link>
          </div>
        </Container>
      </section>

      {/* REMOVE RISK */}
      <section className="bg-white py-16">
        <Container>
          <div className="max-w-xl mx-auto text-center rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-8">
            <Info size={20} className="text-[#10B981] mx-auto mb-3" />
            <p className="text-[#334155] leading-relaxed">You don't need to decide today. Look through the concept. Show it to your team. Tell us what you like, tell us what you hate, ask questions. If it makes sense for the business, we'll discuss how to turn it into the real thing.</p>
          </div>
        </Container>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#0F172A] py-20 md:py-24">
        <Container className="text-center">
          <HeartHandshake size={28} className="text-[#34D399] mx-auto mb-5" />
          <h2 className="text-3xl sm:text-4xl font-bold font-display text-white text-balance max-w-xl mx-auto">Your business already exists. Let's make sure it looks like it does.</h2>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to={contactHref} data-testid="wtm-cta-final-discuss" className="inline-flex items-center gap-2 rounded-xl bg-[#10B981] px-7 py-3.5 text-sm font-semibold text-white hover:bg-[#059669] transition-colors">
              Discuss my website concept <ArrowRight size={16} />
            </Link>
            {hook.conceptPath && (
              <a href={hook.conceptPath} target="_blank" rel="noopener noreferrer" data-testid="wtm-cta-final-return" className="inline-flex items-center gap-2 rounded-xl border border-white/20 px-7 py-3.5 text-sm font-semibold text-white hover:bg-white/5 transition-colors">
                Return to my concept
              </a>
            )}
          </div>
          <p className="mt-5 text-xs text-slate-400">No obligation · Talk directly with MySiteCare</p>
        </Container>
      </section>
    </div>
  );
}
